import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";

export interface FormModalProps {
  open: boolean;
  onClose: () => void;
  /** Title (e.g. "New client" / "Edit client") */
  title: string;
  /** Form fields */
  children: React.ReactNode;
  /** Primary button label */
  submitLabel?: string;
  /** Called when primary button is clicked */
  onSubmit: () => void;
  /** Disable submit (e.g. validation) */
  submitDisabled?: boolean;
  /** Show loading state on submit button */
  loading?: boolean;
  /** Cancel button label */
  cancelLabel?: string;
  /** maxWidth: xs, sm, md, lg */
  maxWidth?: "xs" | "sm" | "md" | "lg";
}

/**
 * Consistent modal for create/edit forms across the app.
 * Use for clients, providers, locations, and any entity form.
 */
export default function FormModal({
  open,
  onClose,
  title,
  children,
  submitLabel = "Save",
  onSubmit,
  submitDisabled = false,
  loading = false,
  cancelLabel = "Cancel",
  maxWidth = "sm",
}: FormModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          mt: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          pt: 2.5,
          pb: 2.5,
          px: 3,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 3.5,
          pb: 2,
          px: 3,
          overflowY: "auto",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {children}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexShrink: 0, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={submitDisabled || loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {loading ? "Saving…" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
